/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";

import { mergeProps, useFocus, useId, useKeyboard } from "react-aria";

import { cssFns } from "@superweb/css";

import { useMessage } from "#intl";

import { useTypo } from "../typo";
import { useUiColors } from "../theme";
import { InternalButton } from "../buttons/internal-button";
import { FieldErrorMessage } from "../fields/error-message";
import { FieldDescription } from "../fields/description";
import { icons } from "..";
import { useSubmitGuard } from "../experimental-form";
import { useUiOptions } from "../ui-options-context";

type InvalidRules = {
  min: boolean;
  max: boolean;
};

export type CounterState = {
  /**
   * Current input's value.
   */
  value: number;

  /**
   * Is set to `true` on change if the field is invalid.
   */
  invalid?: boolean;

  /**
   * When set `false` the error message is not visible even if is set.
   * Is set on change depending on the interaction.
   * Can be set externally to force hide/show the error message.
   * The field has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorVisible?: boolean;

  /**
   * The error message associated with the field.
   * Visible only when `errorVisible` is `true`.
   * The field has invalid state when both the `errorMessage` is not empty and `errorVisible` is `true`.
   */
  errorMessage?: string;
};

const supportsFieldSizing =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content");

/**
 * Counter is a user interface element that includes a numeric value display and "+" and "-" buttons.
 * It allows users to easily increment or decrement the displayed value.
 * Counter is designed for entering small integer numbers that fit within the input's visible width.
 *
 * With keyboard input enabled, the input width follows the number of characters (and grows as the user types), up to the available space. Longer values are clipped inside the field.
 */
export const Counter = ({
  label,
  description,
  ariaDescribedBy,
  disabled,
  min = 0,
  max,
  state,
  onChange,
  validate,
}: {
  /**
   * Text for field's label, that describes field's meaning.
   * No need to specify input examples in label's text.
   * The label is used for the accessibility of the element
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/label
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
   * https://www.w3.org/WAI/tutorials/forms/labels/
   */
  label: string;

  /**
   * Text for field's description, that describes in detail the purpose of this field.
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-description
   */
  description?: string;

  /**
   * Text for field's description, that describes in detail the purpose of this field.
   * Use `ariaDescribedBy` when you need to use an element with text and additional content
   * (e.g., icons, images, or formatting) to describe the field.
   * This allows for associating the field with rich, external descriptions that provide additional context.
   *
   * Links:
   * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby
   */
  ariaDescribedBy?: string;

  /**
   * 	If `true`, the component is disabled.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * The minimum allowed value that a user may enter.
   * @defaultValue 0
   */
  min?: number;

  /**
   * The maximum allowed value that a user may enter.
   */
  max?: number;

  /**
   * Current field's state.
   * The state stores the interactive fields, that change when interacting with the component.
   */
  state: CounterState;

  /**
   * Callback fired when the state is changed.
   * @param state - Recommended state for the component after the change.
   */
  onChange: (state: CounterState) => void;

  /**
   * Custom validation function.
   * Called during validation to provide additional field validation beyond built-in rules.
   * Custom validation error messages take precedence over built-in error messages.
   *
   * @param state - Current Counter state.
   * @returns Error message to display when invalid, or `undefined` when valid.
   */
  validate?: (state: CounterState) => string | undefined;
}) => {
  const typo = useTypo();
  const uiColors = useUiColors();
  const uiOptions = useUiOptions();
  const enableKeyboardInput = uiOptions.experimental?.counterKeyboardInput;

  const descriptionId = useId();
  const labelId = useId();
  const inputId = useId();

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(String(state.value));
  const inputRef = useRef<HTMLInputElement>(null);

  const { focusProps } = useFocus({
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      setIsFocused(false);
    },
  });

  const getErrorMessage = useErrorMessage();

  const handleValidate = (nextState: CounterState) => {
    const invalidRules = validateCounterValue({
      value: nextState.value,
      min,
      max,
    });
    const customErrorMessage = validate?.(nextState);
    const errorMessage = Object.values(invalidRules).some(Boolean)
      ? getErrorMessage({
          min,
          max,
          invalidRules,
        })
      : undefined;

    return {
      invalidRules: {
        ...invalidRules,
        custom: Boolean(customErrorMessage),
      },
      errorMessage: customErrorMessage || errorMessage,
    };
  };

  const handleChange = (nextState: CounterState) => {
    const { invalidRules, errorMessage } = handleValidate(nextState);

    onChange({
      value: nextState.value,
      errorVisible: nextState.errorVisible,
      invalid: Object.values(invalidRules).some(Boolean),
      errorMessage,
    });
  };

  useSubmitGuard({
    validate: () => {
      const { invalidRules } = handleValidate(state);
      return !Object.values(invalidRules).some(Boolean);
    },
    onInvalid: () => handleChange({ ...state, errorVisible: true }),
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Tab" || disabled) {
      return;
    }

    // Allow standard editing keys for enableKeyboardInput
    if (
      enableKeyboardInput &&
      (e.key.length === 1 ||
        e.code === "Backspace" ||
        e.code === "Delete" ||
        e.code === "ArrowLeft" ||
        e.code === "ArrowRight")
    ) {
      return;
    }

    e.preventDefault();

    switch (e.code) {
      case "ArrowDown":
        if (state.value > min) {
          const newValue = state.value - 1;
          if (enableKeyboardInput) {
            setInputValue(String(newValue));
          }
          handleChange({
            ...state,
            value: newValue,
            errorVisible: false,
          });
        }
        break;
      case "ArrowUp":
        if (!max || state.value < max) {
          const newValue = state.value + 1;
          if (enableKeyboardInput) {
            setInputValue(String(newValue));
          }
          handleChange({
            ...state,
            value: newValue,
            errorVisible: false,
          });
        }
        break;
      case "Home":
        if (enableKeyboardInput) {
          setInputValue(String(min));
        }
        handleChange({
          ...state,
          value: min,
          errorVisible: false,
        });
        break;
      case "End":
        if (max) {
          if (enableKeyboardInput) {
            setInputValue(String(max));
          }
          handleChange({
            ...state,
            value: max,
            errorVisible: false,
          });
        }
        break;
      case "PageDown": {
        const newValue = state.value - 5 < min ? min : state.value - 5;
        if (enableKeyboardInput) {
          setInputValue(String(newValue));
        }
        handleChange({
          ...state,
          value: newValue,
          errorVisible: false,
        });
        break;
      }
      case "PageUp": {
        const newValue = max && state.value + 5 > max ? max : state.value + 5;
        if (enableKeyboardInput) {
          setInputValue(String(newValue));
        }
        handleChange({
          ...state,
          value: newValue,
          errorVisible: false,
        });
        break;
      }
      default:
        break;
    }
  };

  const { keyboardProps } = useKeyboard({
    onKeyDown: handleKeyDown,
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Allow only digits and empty string
    if (newValue === "" || /^\d+$/.test(newValue)) {
      setInputValue(newValue);

      if (newValue === "") {
        // If empty string, set to min
        handleChange({
          ...state,
          value: min,
          errorVisible: false,
        });
      } else {
        const parsedValue = parseInt(newValue, 10);

        // Check if value is out of range to show error immediately
        handleChange({
          ...state,
          value: parsedValue,
          errorVisible:
            parsedValue < min || (max !== undefined && parsedValue > max),
        });
      }
    }
  };

  // Synchronize inputValue when state.value changes externally
  if (String(state.value) !== inputValue && !isFocused) {
    setInputValue(String(state.value));
  }

  return (
    <div
      role="group"
      css={{
        display: "grid",
        opacity: disabled ? "0.5" : "1",
      }}
    >
      <div
        css={{
          display: "inline-grid",
          justifyContent: "space-between",
          alignItems: "center",
          gridAutoFlow: "column",
          columnGap: "16px",
          backgroundColor: uiColors.controlMinor,
          boxSizing: "border-box",
          alignContent: "center",
          paddingInlineStart: "16px",
          paddingBlockStart: "8px",
          paddingBlockEnd: "8px",
          ...cssFns.border({ radius: "16px" }),
          ...cssFns.boxShadow(
            isFocused &&
              !enableKeyboardInput && {
                spreadRadius: "2px",
                color: uiColors.focus,
              },
          ),
        }}
      >
        <label
          id={labelId}
          css={{
            ...cssFns.overflow("hidden"),
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            ...typo({ level: "body2", weight: "regular", density: "tight" }),
            color: uiColors.text,
          }}
        >
          {label}
        </label>
        <div
          css={{
            height: "40px",
            alignItems: "center",
            display: "grid",
            gridTemplateColumns: "48px minmax(48px, max-content) 48px",
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <InternalButton
              ariaLabel="decrease"
              ariaButtonProps={{
                excludeFromTabOrder: true,
                "aria-controls": enableKeyboardInput ? inputId : undefined,
              }}
              variant="ghost"
              size="xs"
              icon={icons.MinusL}
              disabled={disabled || state.value === min}
              onPress={() => {
                const newValue = state.value - 1;
                setInputValue(String(newValue));
                handleChange({
                  ...state,
                  value: newValue,
                  errorVisible: true,
                });
              }}
            />
          </div>
          {enableKeyboardInput ? (
            <input
              {...mergeProps(keyboardProps, focusProps, {
                onChange: handleInputChange,
              })}
              ref={inputRef}
              id={inputId}
              role="spinbutton"
              type="text"
              inputMode="numeric"
              aria-valuenow={state.value}
              aria-valuemin={min}
              aria-valuemax={max}
              aria-labelledby={labelId}
              aria-describedby={[descriptionId, ariaDescribedBy]
                .filter(Boolean)
                .join(" ")}
              aria-disabled={disabled}
              aria-invalid={Boolean(state.errorVisible && state.errorMessage)}
              value={inputValue}
              disabled={disabled}
              css={{
                outlineStyle: "none",
                textAlign: "center",
                ...cssFns.border({
                  width: "2px",
                  radius: "12px",
                  style: "solid",
                  color: isFocused ? uiColors.text : "transparent",
                }),
                backgroundColor: isFocused
                  ? uiColors.background
                  : "transparent",
                height: "40px",
                // Fallback for Firefox and Safari: adjust input width.
                width: supportsFieldSizing
                  ? "auto"
                  : `${Math.max(inputValue.length, 1)}ch`,
                fieldSizing: supportsFieldSizing ? "content" : undefined,
                minWidth: "48px",
                maxWidth: "100%",
                boxSizing: "border-box",
                ...cssFns.padding("0"),
                ...typo({
                  level: "body1",
                  weight: "regular",
                  density: "tight",
                }),
                color: uiColors.text,
                cursor: disabled ? "not-allowed" : "text",
                // Width limitation and overflow clipping
                ...cssFns.overflow("hidden"),
                textOverflow: "clip",
              }}
            />
          ) : (
            <span
              {...mergeProps(keyboardProps, focusProps, {
                onBlur: () => {
                  handleChange({
                    ...state,
                    errorVisible: true,
                  });
                },
              })}
              tabIndex={0}
              role="spinbutton"
              aria-valuenow={state.value}
              aria-valuemin={min}
              aria-valuemax={max}
              aria-labelledby={labelId}
              aria-describedby={[descriptionId, ariaDescribedBy]
                .filter(Boolean)
                .join(" ")}
              aria-disabled={disabled}
              aria-invalid={Boolean(state.errorVisible && state.errorMessage)}
              css={{
                outlineWidth: "0",
                display: "flex",
                justifyContent: "center",
                ...typo({
                  level: "body1",
                  weight: "regular",
                  density: "tight",
                }),
                color: uiColors.text,
              }}
            >
              {state.value}
            </span>
          )}
          <div
            css={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <InternalButton
              ariaLabel="increase"
              ariaButtonProps={{
                excludeFromTabOrder: true,
                "aria-controls": enableKeyboardInput ? inputId : undefined,
              }}
              variant="ghost"
              size="xs"
              icon={icons.PlusL}
              disabled={disabled || state.value === max}
              onPress={() => {
                const newValue = state.value + 1;
                setInputValue(String(newValue));
                handleChange({
                  ...state,
                  value: newValue,
                  errorVisible: true,
                });
              }}
            />
          </div>
        </div>
      </div>
      {state.errorVisible && state.errorMessage ? (
        <FieldErrorMessage errorMessageProps={{ id: descriptionId }}>
          {state.errorMessage}
        </FieldErrorMessage>
      ) : (
        description && (
          <FieldDescription descriptionProps={{ id: descriptionId }}>
            {description}
          </FieldDescription>
        )
      )}
    </div>
  );
};

export const createCounterState = (
  defaultValue?: CounterState,
): CounterState => {
  return {
    value: 0,
    invalid: false,
    errorVisible: false,
    errorMessage: undefined,
    ...defaultValue,
  };
};

export const useCounterState = (defaultValue?: CounterState) => {
  return useState<CounterState>(createCounterState(defaultValue));
};

const validateCounterValue = ({
  value,
  min,
  max,
}: {
  value: number;
  min?: number;
  max?: number;
}): InvalidRules => {
  const invalid = {
    min: false,
    max: false,
  };

  if (min !== undefined) {
    invalid.min = value < min;
  }

  if (max !== undefined) {
    invalid.max = value > max;
  }

  return invalid;
};

const useErrorMessage = () => {
  const message = useMessage();

  return ({
    min,
    max,
    invalidRules,
  }: {
    min?: number;
    max?: number;
    invalidRules: InvalidRules;
  }): string | undefined => {
    if (invalidRules.max && max !== undefined) {
      return message({
        id: "f8451dbe-31b1-4c3d-9691-ea7d9a140319",
        context: "Counter. Error message in the Counter component.",
        default: "Value can't be greater than {max}",
        values: {
          max,
        },
      });
    }

    if (invalidRules.min && min !== undefined) {
      return message({
        id: "db8096db-0845-46b6-a1a5-6e2015ebc4bc",
        context: "Counter. Error message in the Counter component.",
        default: "Value can't be less than {min}",
        values: {
          min,
        },
      });
    }

    return undefined;
  };
};
